import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: BASE });

export const placesAutocomplete = (input, sessionToken, language = "de") =>
  api.post("/places/autocomplete", { input, session_token: sessionToken, language }).then((r) => r.data.predictions);

export const reverseGeocode = (lat, lng, language = "de") =>
  api.get("/places/reverse", { params: { lat, lng, language } }).then((r) => r.data);

export const getQuote = (payload) => api.post("/quote", payload).then((r) => r.data);

export const createQuoteLink = (params) => api.post("/quote-link", params).then((r) => r.data);
export const getQuoteLink = (code) => api.get(`/quote-link/${code}`).then((r) => r.data);


export const createBooking = (payload) =>
  api.post("/bookings", { ...payload, origin_url: window.location.origin }).then((r) => r.data);

// Customer accounts
const custCfg = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
export const customerRegister = (payload) => api.post("/customers/register", payload).then((r) => r.data);
export const customerLogin = (payload) => api.post("/customers/login", payload).then((r) => r.data);
export const customerGoogle = (credential) => api.post("/customers/google", { credential }).then((r) => r.data);
export const customerEmergentSession = (session_id) => api.post("/customers/emergent-session", { session_id }).then((r) => r.data);
export const customerAuthConfig = () => api.get("/customers/auth-config").then((r) => r.data).catch(() => ({ google: false, apple: false }));
export const customerMe = (token) => api.get("/customers/me", custCfg(token)).then((r) => r.data);
export const customerUpdate = (token, payload) => api.put("/customers/me", payload, custCfg(token)).then((r) => r.data);
export const customerBookings = (token) => api.get("/customers/me/bookings", custCfg(token)).then((r) => r.data);

export const sendContact = (payload) => api.post("/contact", payload).then((r) => r.data);

// Google Ads click-fraud tracking + traffic analytics + abandoned-booking leads
export const trackVisit = (payload) =>
  api.post("/track/visit", payload).then((r) => r.data).catch(() => ({ blocked: false }));
export const trackStatus = () =>
  api.get("/track/status").then((r) => r.data).catch(() => ({ blocked: false }));
export const createLead = (payload) =>
  api.post("/leads", payload).then((r) => r.data).catch(() => ({ ok: false }));

export const sendChat = (session_id, message, lang = "de") =>
  api.post("/chat", { session_id, message, lang }).then((r) => r.data);

// Voice → Booking (OpenAI Whisper)
const _audioExt = (t = "") =>
  t.includes("mp4") ? "mp4" : t.includes("ogg") ? "ogg" : t.includes("wav") ? "wav" : t.includes("mpeg") ? "mp3" : "webm";

export const voiceBooking = (blob, lang) => {
  const fd = new FormData();
  fd.append("audio", blob, `voice.${_audioExt(blob.type)}`);
  if (lang) fd.append("lang", lang);
  return api.post("/voice/booking", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const voiceTranscribe = (blob, lang) => {
  const fd = new FormData();
  fd.append("audio", blob, `voice.${_audioExt(blob.type)}`);
  if (lang) fd.append("lang", lang);
  return api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const synthesizeSpeech = (text, lang = "de") =>
  api.post("/tts", { text, lang }, { responseType: "blob" }).then((r) => URL.createObjectURL(r.data));

export const payBooking = (booking_id) =>
  api.post(`/bookings/${booking_id}/pay`, { origin_url: window.location.origin }).then((r) => r.data);

export const payBookingEmbedded = (booking_id) =>
  api.post(`/bookings/${booking_id}/pay-embedded`, { origin_url: window.location.origin }).then((r) => r.data);

export const getBookingPaymentStatus = (booking_id) =>
  api.get(`/bookings/${booking_id}/payment-status`).then((r) => r.data);

export const notifyPaymentFailed = (booking_id) =>
  api.post(`/bookings/${booking_id}/payment-failed`, {}).then((r) => r.data);

export const switchBookingToCash = (booking_id) =>
  api.post(`/bookings/${booking_id}/switch-to-cash`, {}).then((r) => r.data);

export const getFlightStatus = (code, date) =>
  api.get(`/flight/${encodeURIComponent(code)}`, { params: date ? { flight_date: date } : {} }).then((r) => r.data);

export const lookupBooking = (ref) =>
  api.get(`/bookings/lookup/${encodeURIComponent(ref)}`).then((r) => r.data);

export const invoiceUrl = (ref) => `${BASE}/bookings/invoice/${encodeURIComponent(ref)}`;
export const driverShareInfo = (ref, t) => api.get(`/drive/${encodeURIComponent(ref)}`, { params: { t } }).then((r) => r.data);
export const postDriverLocation = (payload) => api.post(`/driver/location`, payload).then((r) => r.data);
export const tipDriver = (ref, amount) =>
  api.post(`/bookings/${encodeURIComponent(ref)}/tip`, { amount, origin_url: window.location.origin }).then((r) => r.data);
export const tipStatus = (ref) => api.get(`/bookings/${encodeURIComponent(ref)}/tip/status`).then((r) => r.data);
export const adminSendInvoice = (token, ref) => api.post(`/admin/bookings/${encodeURIComponent(ref)}/send-invoice`, {}, authCfg(token)).then((r) => r.data);
export const adminSendBookingMeetingPoint = (token, id) => api.post(`/admin/bookings/${encodeURIComponent(id)}/send-meeting-point`, {}, authCfg(token)).then((r) => r.data);
export const giftcardUrl = (ref) => `${BASE}/bookings/${encodeURIComponent(ref)}/giftcard`;

export const adminLogin = (email, password) =>
  api.post("/admin/login", { email, password }).then((r) => r.data);

const authCfg = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const adminBookings = (token) =>
  api.get("/admin/bookings", authCfg(token)).then((r) => r.data);

export const adminSearchLogs = (token) =>
  api.get("/admin/search-logs", authCfg(token)).then((r) => r.data);

export const adminAdVisits = (token) =>
  api.get("/admin/ad-visits", authCfg(token)).then((r) => r.data);
export const adminWhitelistIp = (token, ip) =>
  api.post(`/admin/ad-visits/${encodeURIComponent(ip)}/whitelist`, {}, authCfg(token)).then((r) => r.data);
export const adminBlockIp = (token, ip) =>
  api.post(`/admin/ad-visits/${encodeURIComponent(ip)}/block`, {}, authCfg(token)).then((r) => r.data);
export const adminLeads = (token) =>
  api.get("/admin/leads", authCfg(token)).then((r) => r.data);

export const adminTraffic = (token, days = 30) =>
  api.get("/admin/traffic", { params: { days }, ...authCfg(token) }).then((r) => r.data);

export const adminApprove = (token, id) =>
  api.post(`/admin/bookings/${id}/approve`, {}, authCfg(token)).then((r) => r.data);

export const adminReject = (token, id) =>
  api.post(`/admin/bookings/${id}/reject`, {}, authCfg(token)).then((r) => r.data);

export const adminCancel = (token, id) =>
  api.post(`/admin/bookings/${id}/cancel`, {}, authCfg(token)).then((r) => r.data);

export const adminReconcilePayment = (token, ref) =>
  api.post(`/admin/bookings/${encodeURIComponent(ref)}/reconcile-payment`, {}, authCfg(token)).then((r) => r.data);

export const adminEditBooking = (token, id, payload) =>
  api.put(`/admin/bookings/${id}`, payload, authCfg(token)).then((r) => r.data);

// ---- Admin webmail (IMAP/SMTP) ----
export const mailAccounts = (token) => api.get(`/admin/mail/accounts`, authCfg(token)).then((r) => r.data);
export const mailAddAccount = (token, payload) => api.post(`/admin/mail/accounts`, payload, authCfg(token)).then((r) => r.data);
export const mailDeleteAccount = (token, id) => api.delete(`/admin/mail/accounts/${id}`, authCfg(token)).then((r) => r.data);
export const mailMessages = (token, id, folder = "INBOX", limit = 30) =>
  api.get(`/admin/mail/${id}/messages`, { ...authCfg(token), params: { folder, limit } }).then((r) => r.data);
export const mailMessage = (token, id, uid, folder = "INBOX") =>
  api.get(`/admin/mail/${id}/message/${uid}`, { ...authCfg(token), params: { folder } }).then((r) => r.data);
export const mailSend = (token, payload) => api.post(`/admin/mail/send`, payload, authCfg(token)).then((r) => r.data);
export const mailUnread = (token) => api.get(`/admin/mail/unread`, authCfg(token)).then((r) => r.data);
export const mailParse = (token, payload) => api.post(`/admin/mail/parse`, payload, authCfg(token)).then((r) => r.data);
export const mailMessageAction = (token, payload) => api.post(`/admin/mail/message-action`, payload, authCfg(token)).then((r) => r.data);
export const createBookingFromEmail = (token, payload) => api.post(`/admin/bookings/from-email`, payload, authCfg(token)).then((r) => r.data);
export const sendMeetingPoint = (token, payload) => api.post(`/admin/mail/send-meeting-point`, payload, authCfg(token)).then((r) => r.data);
export const getQrSettings = (token) => api.get(`/admin/qr-settings`, authCfg(token)).then((r) => r.data);
export const saveQrSettings = (token, payload) => api.post(`/admin/qr-settings`, payload, authCfg(token)).then((r) => r.data);
export const createRechnung = (token, payload) => api.post(`/admin/rechnung`, payload, authCfg(token)).then((r) => r.data);
export const listRechnungen = (token) => api.get(`/admin/rechnungen`, authCfg(token)).then((r) => r.data);
export const emailRechnung = (token, id, payload) => api.post(`/admin/rechnung/${id}/email`, payload, authCfg(token)).then((r) => r.data);
export const emailRequests = (token, status = "active") => api.get(`/admin/email-requests`, { ...authCfg(token), params: { status } }).then((r) => r.data);
export const confirmEmailRequest = (token, id) => api.post(`/admin/email-requests/${id}/confirm`, {}, authCfg(token)).then((r) => r.data);
export const dismissEmailRequest = (token, id) => api.post(`/admin/email-requests/${id}/dismiss`, {}, authCfg(token)).then((r) => r.data);
export const convertEmailRequest = (token, id, payload) => api.post(`/admin/email-requests/${id}/convert`, payload, authCfg(token)).then((r) => r.data);
export const mailAiDraft = (token, payload) => api.post(`/admin/mail/ai-draft`, payload, authCfg(token)).then((r) => r.data);

export const adminVehicles = (token) =>
  api.get("/admin/vehicles", authCfg(token)).then((r) => r.data);

export const adminUpdateVehicle = (token, id, payload) =>
  api.put(`/admin/vehicles/${id}`, payload, authCfg(token)).then((r) => r.data);

export const getAddons = () => api.get("/addons").then((r) => r.data);
export const adminAddons = (token) => api.get("/admin/addons", authCfg(token)).then((r) => r.data);
export const adminAddAddon = (token, payload) => api.post("/admin/addons", payload, authCfg(token)).then((r) => r.data);
export const adminUpdateAddon = (token, id, payload) => api.put(`/admin/addons/${id}`, payload, authCfg(token)).then((r) => r.data);
export const adminDeleteAddon = (token, id) => api.delete(`/admin/addons/${id}`, authCfg(token)).then((r) => r.data);
export const rateBooking = (ref, payload) => api.post(`/bookings/${ref}/rate`, payload).then((r) => r.data);
export const getReviews = () => api.get("/reviews").then((r) => r.data);
export const getReviewsSummary = () => api.get("/reviews/summary").then((r) => r.data);
export const adminRevenue = (token, days = 90) =>
  api.get("/admin/revenue", { params: { days }, ...authCfg(token) }).then((r) => r.data);
export const adminRatingSample = (token, to) => api.post("/admin/rating/sample", { to: to || "" }, authCfg(token)).then((r) => r.data);
export const adminRatingBackfillCount = (token) => api.get("/admin/rating/backfill-count", authCfg(token)).then((r) => r.data);
export const adminRatingBackfill = (token) => api.post("/admin/rating/backfill", {}, authCfg(token)).then((r) => r.data);
export const adminWaReviewRecipients = (token, onlyCompleted = true) =>
  api.get("/admin/whatsapp/review-recipients", { params: { only_completed: onlyCompleted }, ...authCfg(token) }).then((r) => r.data);
export const adminWaReviewMark = (token, phone) => api.post("/admin/whatsapp/review-sent", { phone }, authCfg(token)).then((r) => r.data);
export const adminRatingSendOne = (token, email, name) => api.post("/admin/rating/send-one", { email, name: name || "" }, authCfg(token)).then((r) => r.data);
export const adminEmailMarketing = (token) => api.get("/admin/email-marketing", authCfg(token)).then((r) => r.data);
export const adminSetOptout = (token, email, optout) => api.post("/admin/email-marketing/optout", { email, optout }, authCfg(token)).then((r) => r.data);
export const adminSetOptoutAll = (token, optout) => api.post("/admin/email-marketing/optout-all", { optout }, authCfg(token)).then((r) => r.data);
export const adminAddMarketingEmail = (token, email, name) => api.post("/admin/email-marketing/add", { email, name }, authCfg(token)).then((r) => r.data);
export const adminDeleteMarketingEmail = (token, email) => api.delete(`/admin/email-marketing/${encodeURIComponent(email)}`, authCfg(token)).then((r) => r.data);
export const adminSendTestCampaign = (token, email) => api.post("/admin/email-marketing/send-test", { email }, authCfg(token)).then((r) => r.data);
export const adminGetDiscountRules = (token) => api.get("/admin/discount-rules", authCfg(token)).then((r) => r.data);
export const adminCreateDiscountRule = (token, rule) => api.post("/admin/discount-rules", rule, authCfg(token)).then((r) => r.data);
export const adminUpdateDiscountRule = (token, id, rule) => api.put(`/admin/discount-rules/${id}`, rule, authCfg(token)).then((r) => r.data);
export const adminDeleteDiscountRule = (token, id) => api.delete(`/admin/discount-rules/${id}`, authCfg(token)).then((r) => r.data);
export const adminAdStatsDaily = (token) => api.get("/admin/ad-stats/daily", authCfg(token)).then((r) => r.data);
export const indexnowSubmitAll = () => api.post("/indexnow/submit-all").then((r) => r.data);
export const campaignSend = (token, payload) => api.post("/admin/campaign/send", payload, authCfg(token)).then((r) => r.data);
export const adminCampaigns = (token) => api.get("/admin/campaigns", authCfg(token)).then((r) => r.data);
export const campaignCancel = (token, cid) => api.post(`/admin/campaign/${cid}/cancel`, {}, authCfg(token)).then((r) => r.data);
export const campaignGenerateImage = (token, prompt) => api.post("/admin/campaign/generate-image", { prompt }, authCfg(token)).then((r) => r.data);
export const campaignUploadImage = (token, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/campaign/upload-image", fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const adminGetAdSettings = (token) => api.get("/admin/ad-settings", authCfg(token)).then((r) => r.data);
export const adminUpdateAdSettings = (token, avg_cpc) => api.put("/admin/ad-settings", { avg_cpc }, authCfg(token)).then((r) => r.data);
export const adminGetMonthlyReport = (token) => api.get("/admin/reports/monthly", authCfg(token)).then((r) => r.data);
export const adminSendMonthlyReport = (token, email) => api.post("/admin/reports/monthly/send-now", { email }, authCfg(token)).then((r) => r.data);
export const adminPricePreview = (token, payload) => api.post("/admin/price-preview", payload, authCfg(token)).then((r) => r.data);
export const adminCopyPricingToAll = (token, vid) => api.post(`/admin/vehicles/${vid}/copy-pricing-to-all`, {}, authCfg(token)).then((r) => r.data);
export const adminBlogGenerate = (token, city, keyword) => api.post("/admin/blog/generate", { city, keyword }, authCfg(token)).then((r) => r.data);
export const adminListGeneratedBlog = (token) => api.get("/blog/generated", authCfg(token)).then((r) => r.data);
export const adminDeleteGeneratedBlog = (token, slug) => api.delete(`/admin/blog/generated/${slug}`, authCfg(token)).then((r) => r.data);
export const getGeneratedBlogPost = (slug) => api.get(`/blog/generated/${slug}`).then((r) => r.data);
export const adminGetPricingSettings = (token) => api.get("/admin/pricing-settings", authCfg(token)).then((r) => r.data);
export const adminUpdatePricingSettings = (token, payload) => api.put("/admin/pricing-settings", payload, authCfg(token)).then((r) => r.data);
export const adminGetCompanySettings = (token) => api.get("/admin/company-settings", authCfg(token)).then((r) => r.data);
export const adminSaveCompanySettings = (token, payload) => api.post("/admin/company-settings", payload, authCfg(token)).then((r) => r.data);
export const adminInvoicePreview = (token, payload) => api.post("/admin/invoice-preview", payload, { ...authCfg(token), responseType: "blob" }).then((r) => r.data);
export const adminUploadCompanyLogo = (token, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/company-logo", fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const adminDrivers = (token) =>
  api.get("/admin/drivers", authCfg(token)).then((r) => r.data);

export const adminAddDriver = (token, payload) =>
  api.post("/admin/drivers", payload, authCfg(token)).then((r) => r.data);

export const adminDeleteDriver = (token, id) =>
  api.delete(`/admin/drivers/${id}`, authCfg(token)).then((r) => r.data);

export const adminAssignDriver = (token, id, driver_id) =>
  api.post(`/admin/bookings/${id}/assign`, { driver_id }, authCfg(token)).then((r) => r.data);

export const adminSendWhatsapp = (token, id) =>
  api.post(`/admin/bookings/${id}/whatsapp`, {}, authCfg(token)).then((r) => r.data);

export const createCheckout = (booking_id) =>
  api
    .post("/checkout/session", { booking_id, origin_url: window.location.origin })
    .then((r) => r.data);

export const getCheckoutStatus = (sessionId) =>
  api.get(`/checkout/status/${sessionId}`).then((r) => r.data);

export const getCityContent = (slug, name, region, lang = "de") =>
  api.get(`/city-content/${slug}`, { params: { name, region, lang } }).then((r) => r.data);

export const getServiceContent = (slug) =>
  api.get(`/service-content/${slug}`).then((r) => r.data);
